import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, concat, forkJoin, Observable } from 'rxjs';
import { defaultIfEmpty, map, switchMap, tap } from 'rxjs/operators';

import { environment } from '@env/environment';
import { Market } from '@games/entities';
import { IMarket } from '@games/interfaces';
import { MarketEntityService } from '@games/services/market-entity.service';

@Injectable({
  providedIn: 'root',
})
export class MarketService {
  marketsSubject: BehaviorSubject<Market[]>;

  constructor(
    private http: HttpClient,
    private marketEntityService: MarketEntityService
  ) {
    this.marketsSubject = new BehaviorSubject<Market[]>([]);
  }

  getMarket(courseId: number, gameId: number, id: number): Observable<Market> {
    return this.http
      .get<any>(`${environment.apiUrl}courses/${courseId}/games/${gameId}/groupings/${id}/`)
      .pipe(map((res: IMarket) => new Market(res)));
  }

  getMarkets(
    courseId: number,
    gameId: number,
    filters: HttpParams | { [params: string]: string | string[] } = {},
    isSilentLoader = false
  ): Observable<any> {
    return this.http
      .get<any>(`${environment.apiUrl}courses/${courseId}/games/${gameId}/groupings/`, {
        params: filters,
        headers: { 'silent-loader': isSilentLoader ? 'true' : 'false' },
      })
      .pipe(
        map(res => ({ ...res, results: res.results.map((market: IMarket) => new Market(market)) })),
        tap(res =>
          this.marketsSubject.next([
            ...('page' in filters && +filters.page > 1 ? [this.marketsSubject.value] : []),
            ...res.results,
          ])
        )
      );
  }

  createMarket(courseId: number, gameId: number, name: string): Observable<Market> {
    return this.http
      .post<any>(`${environment.apiUrl}courses/${courseId}/games/${gameId}/groupings/`, { name })
      .pipe(map((market: IMarket) => new Market(market)));
  }

  updateMarket(courseId: number, gameId: number, id: number, name: string): Observable<Market> {
    return this.http
      .patch<any>(`${environment.apiUrl}courses/${courseId}/games/${gameId}/groupings/${id}/`, {
        name,
      })
      .pipe(map((market: IMarket) => new Market(market)));
  }

  deleteMarket(courseId: number, gameId: number, id: number) {
    return this.http.delete<any>(
      `${environment.apiUrl}courses/${courseId}/games/${gameId}/groupings/${id}/`,
      { observe: 'response' }
    );
  }

  createCompleteMarket(courseId: number, gameId: number, market: any) {
    return this.createMarket(courseId, gameId, market.name).pipe(
      switchMap(
        (m: Market) =>
          forkJoin(
            market.entities.map((entity, i) =>
              this.marketEntityService.createCompleteEntity(courseId, gameId, m.id, entity)
            )
          )
        //   .pipe(map((entities: MarketEntity[]) => {
        //     m.entities = entities;

        //     return m;
        //   }))
      )
      // tap(m => {
      //   this.marketsSubject.next(
      //     this.marketsSubject.value.map(market => market.name === m.name ? m : market)
      //   );
      // })
    );
  }

  updateCompleteMarket(courseId: number, gameId: number, market: any) {
    const existingMarket = this.marketsSubject.value.find(m => m.id === market.id);

    return concat(
      ...[
        forkJoin([
          ...(existingMarket.name === market.name
            ? []
            : [
                this.updateMarket(courseId, gameId, market.id, market.name),
                // .pipe(tap((m: Market) => {
                //   this.marketsSubject.next(
                //     this.marketsSubject.value.map(market => market.id === m.id ? m : market)
                //   );
                // }))
              ]),
          ...existingMarket.entities
            .filter(entity => market.entities.every(e => e.id !== entity.id))
            .map(entity =>
              this.marketEntityService.deleteEntity(courseId, gameId, market.id, entity.id)
            ),
        ]),
        forkJoin(
          market.entities
            .map((entity, i) => {
              if (entity.id) {
                const existingEntity = existingMarket.entities.find(e => e.id === entity.id);

                if (this.isEntityModified(entity, existingEntity)) {
                  return this.marketEntityService.updateCompleteEntity(
                    courseId,
                    gameId,
                    market.id,
                    entity,
                    existingEntity
                  );
                }
              } else {
                return this.marketEntityService.createCompleteEntity(
                  courseId,
                  gameId,
                  market.id,
                  entity
                );
              }
            })
            .filter(e => e)
        ),
        // .pipe(tap((entities: MarketEntity[]) => {
        //   existingMarket.entities = entities;
        //   this.marketsSubject.next(
        //     this.marketsSubject.value.map(market => market.id === existingMarket.id ? existingMarket : market)
        //   );
        // }))
      ]
    );
  }

  deleteCompleteMarket(courseId: number, gameId: number, marketId: number) {
    return this.deleteMarket(courseId, gameId, marketId);
    // .pipe(
    //   tap(_ => this.marketsSubject.next(this.marketsSubject.value.filter(m => marketId !== m.id)))
    // );
  }

  assignTeam(courseId: number, gameId: number, marketsEntered) {
    return concat(
      ...[
        forkJoin(
          this.getMarketsToDelete(marketsEntered).map(market =>
            this.deleteCompleteMarket(courseId, gameId, market.id)
          )
        ),
        forkJoin([
          ...this.getNewMarkets(marketsEntered).map(market =>
            this.createCompleteMarket(courseId, gameId, market)
          ),
          ...this.getMarketsToUpdate(marketsEntered).map(market =>
            this.updateCompleteMarket(courseId, gameId, market)
          ),
        ]),
      ]
    ).pipe(defaultIfEmpty(null));
  }

  getNewMarkets(marketsEntered) {
    return marketsEntered.filter(market => !market.id);
  }

  getMarketsToUpdate(marketsEntered) {
    return marketsEntered.filter(market => this.isMarketModified(market));
  }

  getMarketsToDelete(marketsEntered) {
    return this.marketsSubject.value.filter(existingMarket =>
      marketsEntered.every(market => market.id !== existingMarket.id)
    );
  }

  isMarketModified(market) {
    return this.marketsSubject.value.some(
      existingMarket =>
        market.id === existingMarket.id &&
        (market.name !== existingMarket.name ||
          !market.entities ||
          !existingMarket.entities ||
          market.entities.length !== existingMarket.entities.length ||
          market.entities.some((entity, i) =>
            this.isEntityModified(
              entity,
              existingMarket.entities.find(e => e.id === entity.id)
            )
          ))
    );
  }

  isEntityModified(entity, existingEntity) {
    return (
      entity.id !== existingEntity.id ||
      entity.name !== existingEntity.name ||
      !entity.players ||
      !existingEntity.players ||
      entity.players.length !== existingEntity.players.length ||
      existingEntity.players.some(existingPlayer => !entity.players.includes(existingPlayer.id)) ||
      entity.players.some(
        player => !existingEntity.players.map(player => player.id).includes(player)
      )
    );
  }
}
